/**
 * A company change that has been SKIPPED — the same row it was in the pending
 * tab, dimmed, with Undo where the actions were.
 *
 * The FILE and the props keep the `Ignored` name: `ignored` is the stored
 * `review_status` these rows are partitioned by, and the identifier naming the
 * wire value it reads is the one thing fu5's rename deliberately leaves alone.
 * What the recruiter sees says "skipped".
 *
 * WHY A SECOND COMPONENT AND NOT A FLAG ON `CompanyChangeRow`
 *  The two share a silhouette and almost nothing else. This one has no
 *  checkbox — there is no update to confirm for a row that will not be
 *  exported — and no Skip, because it already is. Threading `ignored` through
 *  the pending row would make three of its four controls conditional, and the
 *  conditions would not be independent: the checkbox's `confirmed`, its
 *  `onConfirmedChange`, and `onIgnore` all become meaningless together. That is
 *  two components wearing one name.
 *
 *  What they DO share is the shape, and that is the point of this file
 *  existing at all: before it, a skipped company change came back as a full
 *  `W2ConflictCard`, so dismissing a one-line row turned it into a
 *  multi-action card. An archive that re-renders its contents in a different
 *  form is hard to recognise as the thing you put there.
 */

import type { ConflictContact } from "@nu-assist/contracts";
import { Avatar } from "@nu-assist/ui";
import { thriveContactUrl } from "../../lib/thriveLinks";
import {
  LEFT_OUT_PILL,
  UNDO_LEAVE_OUT_LABEL,
  companyTransition,
  formatIgnoredDate,
} from "./conflictActionsCopy";

export interface IgnoredCompanyChangeRowProps {
  contact: ConflictContact;
  /** Presigned S3 GET URL for the source row's stored avatar. */
  resolvedAvatarUrl?: string | null;
  /** Absent when the surface cannot call the persisted APIs (isolated tests). */
  onUndo?: () => void;
}

function fullName(first?: string | null, last?: string | null): string {
  return [first, last].filter(Boolean).join(" ").trim();
}

export function IgnoredCompanyChangeRow({
  contact,
  resolvedAvatarUrl,
  onUndo,
}: IgnoredCompanyChangeRowProps) {
  const id = contact.import_contact_id;
  const name = fullName(contact.first_name, contact.last_name) || "This row";
  const at = contact.left_out_at;

  return (
    <li
      data-testid="ignored-company-change-row"
      id={`conflict-card-${id}`}
      // `opacity-60` is the same dimming the skipped cards carry, so the two
      // shapes read as one state rather than as two treatments of it.
      className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2 opacity-60"
    >
      {/* `md`, the primitive's default, not the `sm` the pending row uses
          (operator, 2026-09-08: "estan muy chicos"). The pending row spends its
          horizontal space on a checkbox, a Skip button and a Thrive link;
          this one has only Undo, so the face can have the room — and a face is
          the whole reason an archived row is recognisable at a glance. */}
      {/* `lg`, matching the CARDS in the same tab — a skipped contact reads
          the same whether it came back as a row or as a card, and the face is
          what makes an archived row recognisable at a glance. The PENDING
          company-change row stays `sm` because it spends its width on a
          checkbox, a Skip button and a Thrive link; this one has only
          Undo. */}
      <Avatar
        src={contact.profile_image_s3_key ? resolvedAvatarUrl : undefined}
        name={name}
        size="lg"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">{name}</p>
        <p className="truncate text-caption text-text-secondary">
          {companyTransition(contact.thrive_company, contact.company)}
        </p>
        <p
          data-testid={`ignored-company-change-pill-${id}`}
          className="truncate text-caption text-text-muted"
        >
          {/* `formatIgnoredDate` already opens with the word, so the pill is
              not printed separately — the cards do the same. Dateless is
              possible (a pre-column row) and falls back to the bare word
              rather than to an invented date. */}
          {at ? formatIgnoredDate(new Date(at), new Date()) : LEFT_OUT_PILL}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Avatar src={contact.thrive_avatar_url} size="lg" />
        {contact.contact_id_thrive && (
          <a
            data-testid={`ignored-company-change-thrive-link-${id}`}
            href={thriveContactUrl(contact.contact_id_thrive)}
            target="_blank"
            rel="noreferrer"
            className="text-caption text-accent hover:underline"
          >
            #{contact.contact_id_thrive} →
          </a>
        )}
      </div>
      {onUndo && (
        <button
          type="button"
          data-testid={`ignored-company-change-undo-${id}`}
          onClick={onUndo}
          className="shrink-0 rounded-md border border-border bg-bg px-2 py-1 text-label text-text hover:bg-surface"
        >
          {UNDO_LEAVE_OUT_LABEL}
        </button>
      )}
    </li>
  );
}
